import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import TopBar from "@/components/TopBar";
import WhatsAppBadge from "@/components/WhatsAppBadge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "254730729729";
const SUPPORT_EMAIL = "support@tandaa.africa";

const PACKAGES = [
  { id: "bronze", label: "Bronze - 25 Mbps / 2,399 KES", monthly: 2399 },
  { id: "silver", label: "Silver - 50 Mbps / 3,599 KES", monthly: 3599 },
  { id: "gold",   label: "Gold - 100 Mbps / 6,999 KES", monthly: 6999 },
];

export default function Order() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const presetLat = params.get("lat");
  const presetLng = params.get("lng");
  const presetPlanId = params.get("plan");

  const initialPlan = PACKAGES.find((p) => p.id === presetPlanId)?.label || PACKAGES[1].label;

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [plan, setPlan] = useState(initialPlan);
  const [status, setStatus] = useState({ kind: "", msg: "" });
  const [busy, setBusy] = useState(false);

  const selectedPkg = PACKAGES.find((p) => p.label === plan) || PACKAGES[1];
  const dueNow = selectedPkg.monthly * 2;
  const dueNowDisplay = dueNow.toLocaleString("en-KE");
  const monthlyDisplay = selectedPkg.monthly.toLocaleString("en-KE");

  const hasPreset = presetLat && presetLng && !isNaN(parseFloat(presetLat)) && !isNaN(parseFloat(presetLng));

  // log the order to backend (best-effort, fire-and-forget) then bounce to WhatsApp
  const persistAndRedirect = async (lat, lng, source) => {
    try {
      await fetch(`${API}/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          phone: phone.trim(),
          plan,
          lat: typeof lat === "number" ? lat : null,
          lng: typeof lng === "number" ? lng : null,
          source,
        }),
      });
    } catch (e) {
      // non-blocking — WhatsApp is the primary channel
      console.warn("order log failed", e);
    }
    const coordsString =
      typeof lat === "number" && typeof lng === "number"
        ? `https://maps.google.com/?q=${lat.toFixed(6)},${lng.toFixed(6)}`
        : `https://maps.google.com/?q=${source.toUpperCase()}`;
    const lines = [
      "Tandaa Order:",
      `Name: ${name || "(unset)"}`,
      `Phone: ${phone || "(unset)"}`,
      `Plan: ${plan}`,
      `You will Pay: ${dueNowDisplay} KES (2 x ${monthlyDisplay} KES)`,
      "Terms: Agreed (Free Install + 2 Months Deposit)",
      `Coordinates: ${coordsString}`,
      `Email CC: ${SUPPORT_EMAIL}`,
    ];
    const text = encodeURIComponent(lines.join("\n"));
    window.location.href = `https://wa.me/${WHATSAPP_NUMBER}?text=${text}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      setStatus({ kind: "error", msg: "Name and phone are required." });
      return;
    }
    setBusy(true);

    if (hasPreset) {
      const lat = parseFloat(presetLat);
      const lng = parseFloat(presetLng);
      setStatus({ kind: "ok", msg: "Using pin from coverage map. Logging order and opening WhatsApp..." });
      persistAndRedirect(lat, lng, "preset");
      return;
    }

    setStatus({ kind: "ok", msg: "Requesting GPS lock..." });

    if (!navigator.geolocation) {
      persistAndRedirect(null, null, "gps_unavailable");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setStatus({ kind: "ok", msg: "Position locked. Logging order and opening WhatsApp..." });
        persistAndRedirect(lat, lng, "live_gps");
      },
      (err) => {
        setStatus({
          kind: "error",
          msg: `GPS denied (${err.code}). Logging order and opening WhatsApp without coordinates.`,
        });
        persistAndRedirect(null, null, "gps_denied");
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  };

  return (
    <div data-testid="order-page">
      <TopBar active="order" sticky />

      <main className="tn-form-wrap">
        <div className="tn-form-eyebrow">/03 — Order Funnel</div>
        <h1 className="tn-form-title">
          Authorize <span className="slash">//</span> Position
          <br /> &amp; Send.
        </h1>

        {hasPreset && (
          <div className="tn-preset-banner" data-testid="preset-banner">
            <span className="tn-preset-eyebrow">Pin attached</span>
            <span className="tn-preset-coords">
              {parseFloat(presetLat).toFixed(5)}, {parseFloat(presetLng).toFixed(5)}
            </span>
            <button
              type="button"
              className="tn-preset-clear"
              onClick={() => navigate("/order.html", { replace: true })}
              data-testid="clear-preset-btn"
            >
              [ use live GPS instead ]
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} data-testid="order-form">
          <div className="tn-field" data-testid="field-name">
            <label htmlFor="name">Name</label>
            <input
              id="name"
              type="text"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Asha Kazungu"
              data-testid="input-name"
              required
            />
          </div>

          <div className="tn-field" data-testid="field-phone">
            <label htmlFor="phone">Phone</label>
            <input
              id="phone"
              type="tel"
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+254 7XX XXX XXX"
              data-testid="input-phone"
              required
            />
          </div>

          <div className="tn-field" data-testid="field-plan">
            <label htmlFor="plan">Plan</label>
            <select
              id="plan"
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
              data-testid="select-plan"
            >
              {PACKAGES.map((p) => (
                <option key={p.id} value={p.label}>{p.label}</option>
              ))}
            </select>
          </div>

          <div className="tn-due-now" data-testid="due-now-block">
            <div className="tn-due-now-label">You will Pay</div>
            <div
              key={plan}
              className="tn-due-now-amount tn-fade-in"
              data-testid="due-now-amount"
            >
              {dueNowDisplay} <small>KES</small>
            </div>
            <div
              key={`${plan}-formula`}
              className="tn-due-now-formula tn-fade-in"
              data-testid="due-now-formula"
            >
              2 × {monthlyDisplay} KES (month 1 + month 2 deposit)
            </div>
          </div>

          <div className="tn-terms" data-testid="terms-block">
            <b>Terms:</b> Free installation. 2-month prepayment required for activation.
            Submitting authorizes Tandaa Networks to receive your name, phone, and current
            GPS coordinates via WhatsApp.
          </div>

          <button
            type="submit"
            className="tn-submit"
            disabled={busy}
            data-testid="authorize-button"
          >
            [ {hasPreset ? "Send Pin + Order via WhatsApp" : "Authorize Position & Send via WhatsApp"} ]
            <span className="arrow">→</span>
          </button>

          {status.msg && (
            <div
              className={`tn-status ${status.kind === "error" ? "is-error" : "is-ok"}`}
              data-testid="order-status"
            >
              {status.msg}
            </div>
          )}
        </form>
      </main>

      <footer className="tn-foot" data-testid="order-footer">
        <div>
          <WhatsAppBadge
            testid="order-footer-whatsapp"
            display="WhatsApp Fallback"
            message="Hi Tandaa — I tried to send my order from tandaa.africa but my GPS pin did not attach. Please pick up here."
          />
        </div>
        <div>{SUPPORT_EMAIL}</div>
        <div><a href="/coverage.html" data-testid="footer-coverage-link">[ ← BACK TO COVERAGE ]</a></div>
      </footer>
    </div>
  );
}
